import {
    ShapeEditorActorContext,
    shapeEditorMachine,
} from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMachine';
import { useContext, useEffect } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import {
    disableScreenPan,
    enableScreenPan,
    getWorldPositionFromScreenPosition,
} from 'app/agriculture/hooks/useCesium/cesiumUtils';
import {
    HALF_HANDLE_ID,
    POINT_ID,
} from 'app/agriculture/forms/components/ShapeEditor/icons/ShapeEditorGraphics';
import { ActorRefFrom } from 'xstate';
import { PrimitiveId } from 'app/agriculture/forms/components/ShapeEditor/hooks/types';
import { getSnappingShapes, snap } from 'app/agriculture/forms/components/ShapeEditor/snapping';

export function useMouseInputs() {
    const actor = ShapeEditorActorContext.useActorRef();
    const {
        mapService: { viewer },
    } = useContext(ServicesContext);
    const isCreating = ShapeEditorActorContext.useSelector((state) => state.matches('Creating'));
    const isEditing = ShapeEditorActorContext.useSelector((state) => state.matches('Editing'));

    useEffect(() => {
        const eventHandler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
        if (isCreating) startHandlingCreateMouseInputs(actor, viewer, eventHandler);
        if (isEditing) startHandlingEditMouseInputs(actor, viewer, eventHandler);

        return () => {
            stopHandlingMouseInputs(eventHandler);
            if (!eventHandler.isDestroyed()) eventHandler.destroy();
        };
    }, [actor, isCreating, isEditing, viewer]);
}

function stopHandlingMouseInputs(eventHandler: Cesium.ScreenSpaceEventHandler) {
    if (!eventHandler.isDestroyed()) {
        eventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        eventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOWN);
        eventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_UP);
        eventHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    }
}

function startHandlingCreateMouseInputs(
    actor: ActorRefFrom<typeof shapeEditorMachine>,
    viewer: Cesium.Viewer,
    eventHandler: Cesium.ScreenSpaceEventHandler,
) {
    const mouseMove = (event: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
        const { snappingShapes, snappingFrameCount, isSnappingEnabled } =
            actor.getSnapshot().context;
        actor.send({
            type: 'createMouseMove',
            position: snap({
                worldPosition: getWorldPositionFromScreenPosition(viewer, event.endPosition),
                snappingShapes,
                isSnappingEnabled,
                viewer,
            }),
            snappingShapes: getSnappingShapes({
                snappingShapes,
                snappingFrameCount,
                windowPosition: event.endPosition,
                viewer,
            }),
        });
    };

    const mouseClick = (event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
        const picks = viewer.scene.drillPick(event.position);
        const hasClickedEnd = picks.some(
            (pick: { id: PrimitiveId }) => pick.id.name === 'drawingEndPoint',
        );
        if (hasClickedEnd) {
            actor.send({ type: 'createClickEndIcon' });
            return;
        }
        const { snappingShapes, isSnappingEnabled } = actor.getSnapshot().context;
        actor.send({
            type: 'createClickMap',
            position: snap({
                worldPosition: getWorldPositionFromScreenPosition(viewer, event.position),
                snappingShapes,
                isSnappingEnabled,
                viewer,
            }),
        });
    };

    if (!eventHandler.isDestroyed()) {
        eventHandler.setInputAction(mouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        eventHandler.setInputAction(mouseClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
}

function startHandlingEditMouseInputs(
    actor: ActorRefFrom<typeof shapeEditorMachine>,
    viewer: Cesium.Viewer,
    eventHandler: Cesium.ScreenSpaceEventHandler,
) {
    const mouseClick = (event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
        const picks = viewer.scene.drillPick(event.position) as {
            id: PrimitiveId;
        }[];
        const clickedPointPicks = picks.filter(
            (pick: { id: PrimitiveId }) => pick.id.name === POINT_ID,
        );
        if (clickedPointPicks.length > 0 && clickedPointPicks[0] != null) {
            actor.send({ type: 'editClickPoint', index: clickedPointPicks[0].id.index });
            return;
        }
        actor.send({ type: 'editClickPoint', index: undefined });
    };

    const mouseDown = (event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
        const picks = viewer.scene.drillPick(event.position) as {
            id: PrimitiveId;
        }[];
        const clickedPointPicks = picks.filter(
            (pick: { id: PrimitiveId }) => pick.id.name === POINT_ID,
        );
        if (clickedPointPicks.length > 0 && clickedPointPicks[0] != null) {
            actor.send({ type: 'editMouseDownPoint', index: clickedPointPicks[0].id.index });
            disableScreenPan(viewer);
            return;
        }
        const clickedHandlePicks = picks.filter(
            (pick: { id: PrimitiveId }) => pick.id.name === HALF_HANDLE_ID,
        );
        if (clickedHandlePicks.length > 0 && clickedHandlePicks[0] != null) {
            const position = getWorldPositionFromScreenPosition(viewer, event.position);
            if (position != null) {
                actor.send({
                    type: 'editMouseDownHalfHandle',
                    index: clickedHandlePicks[0].id.index,
                    position,
                });
                disableScreenPan(viewer);
            }
        }
    };

    const mouseUp = (event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
        const position = getWorldPositionFromScreenPosition(viewer, event.position);
        if (!position) return;
        enableScreenPan(viewer);
        actor.send({ type: 'editDraggingMouseUp', position });
    };

    const mouseMove = (event: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
        const { snappingShapes, snappingFrameCount, isSnappingEnabled } =
            actor.getSnapshot().context;
        actor.send({
            type: 'editDraggingMouseMove',
            position: snap({
                worldPosition: getWorldPositionFromScreenPosition(viewer, event.endPosition),
                snappingShapes,
                isSnappingEnabled,
                viewer,
            }),
            snappingShapes: getSnappingShapes({
                snappingShapes,
                snappingFrameCount,
                windowPosition: event.endPosition,
                viewer,
            }),
        });
    };

    if (!eventHandler.isDestroyed()) {
        eventHandler.setInputAction(mouseClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);
        eventHandler.setInputAction(mouseDown, Cesium.ScreenSpaceEventType.LEFT_DOWN);
        eventHandler.setInputAction(mouseUp, Cesium.ScreenSpaceEventType.LEFT_UP);
        eventHandler.setInputAction(mouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    }
}
